/**
 * Mail ablegen — mit Rückfrage zu den Anhängen (User 03.11.2026: „Beim Drag &
 * Drop einer Mail … soll bei Mails mit Anhängen gefragt werden, ob die Anhänge
 * (zusätzlich) gespeichert werden sollen. Ohne Anhänge: keine Frage.“).
 *
 * Gemeinsam für die Kontakt-Dokumente (`DocumentsSection`) und die Alte
 * Kundenakte (`akteAblage`). Zwei Quellen:
 *
 * - **Mail aus dem KMVS-Postfach**: Die Anhänge werden VOR dem Ablegen über
 *   Graph gezählt (`GET /mail/messages/:id/attachments`, ohne Inline-Bilder).
 *   Gefragt wird, bevor etwas passiert — „Abbrechen“ legt nichts ab.
 *   „Nur die Mail“ legt die `.eml` ab und lässt die Anhänge in ihr (auch die
 *   Sicherung im Hintergrund packt dann nichts aus); einzelne Anhänge
 *   abgewählt → erst die Mail ohne Anhänge, dann die gewählten Anhänge über
 *   denselben Weg wie ein gezogener Anhang.
 * - **Mail-Datei aus Outlook (`.msg`/`.eml`)**: Der Browser kann eine `.msg`
 *   (binäres Outlook-Format) nicht selbst öffnen. Gezählt wird deshalb nach
 *   dem Hochladen über die Mail-Ansicht des Servers; die Frage betrifft nur
 *   noch die Anhänge, die Mail selbst liegt dann schon.
 *
 * Lässt sich nicht zählen (Netzfehler), bleibt es beim bisherigen Verhalten:
 * Mail samt Anhängen.
 */
import { graphMailService, type MailAttachment } from "../../../services/graphMailService";
import { mailFileService } from "../../../services/mailFileService";
import { istMailDatei, mailDateiAuspacken, nachMailDateiUpload } from "../../mail/mailDateiAblage";
import type { AblageErgebnis, AblageZiel, AblageZug } from "../../mail/postfachAblage";
import { frageNachAnhaengen, type AnhangFrage } from "./anhangFrage";
import { legeZugAbSichtbar } from "./zugAblage";
import type { AblagePlatzhalterApi } from "./useAblagePlatzhalter";

type Api = Pick<AblagePlatzhalterApi, "starte" | "fertig" | "scheitern" | "laeuftWieder">;

/** S/MIME-Hülle bzw. Signatur — kein echter Anhang, der Server packt sie aus. */
const istHuelle = (a: { name?: string | null; contentType?: string | null }): boolean =>
    /^smime\.p7[ms]$/i.test(String(a.name ?? "").trim()) || /pkcs7-(mime|signature)/i.test(String(a.contentType ?? ""));

export type PostfachAnhaenge = {
    /** Echte Anhänge (ohne Inline-Bilder und S/MIME-Hülle). */
    anhaenge: MailAttachment[];
    /** Nur eine S/MIME-Hülle sichtbar — was darin steckt, zeigt erst das Auspacken. */
    unbekannt: boolean;
};

/** Die Anhänge einer Postfach-Mail; `null`, wenn sie sich nicht laden ließen. */
export async function postfachMailAnhaenge(mailbox: string, messageId: string): Promise<PostfachAnhaenge | null> {
    try {
        const liste = await graphMailService.fetchAttachments(mailbox, messageId);
        const echt = (liste ?? []).filter((a) => !a.isInline);
        const ohneHuelle = echt.filter((a) => !istHuelle(a));
        return { anhaenge: ohneHuelle, unbekannt: ohneHuelle.length === 0 && echt.length > 0 };
    } catch {
        return null;
    }
}

const verbinde = (a: AblageErgebnis, b: AblageErgebnis): AblageErgebnis => ({
    abgelegt: a.abgelegt + b.abgelegt,
    vorhanden: a.vorhanden + b.vorhanden,
    fehlgeschlagen: [...a.fehlgeschlagen, ...b.fehlgeschlagen],
    dokumentIds: [...(a.dokumentIds ?? []), ...(b.dokumentIds ?? [])],
    laeuft: !!a.laeuft || !!b.laeuft,
    meldung: `${a.meldung} ${b.meldung}`.trim(),
});

/**
 * Legt einen Postfach-Zug ab und fragt bei einer Mail mit Anhängen vorher
 * nach. `null` = abgebrochen, nichts abgelegt. Gezogene Anhänge (kein
 * Mail-Zug) gehen ohne Frage durch.
 */
export async function legeZugAbMitAnhangFrage(
    zug: AblageZug,
    ziel: AblageZiel,
    api: Api,
    neuLaden: () => void | Promise<void>,
    frage: AnhangFrage = frageNachAnhaengen,
): Promise<AblageErgebnis | null> {
    if (zug.art !== "postfachMail") return legeZugAbSichtbar(zug, ziel, api, neuLaden);
    const info = await postfachMailAnhaenge(zug.mail.mailbox, zug.mail.messageId);
    if (!info || (info.anhaenge.length === 0 && !info.unbekannt)) return legeZugAbSichtbar(zug, ziel, api, neuLaden);

    const wahl = await frage({
        mailName: zug.mail.subject || "Ohne Betreff",
        anhaenge: info.anhaenge.map((a) => ({ schluessel: a.id, name: a.name, groesse: a.size })),
        anzahlUnbekannt: info.unbekannt,
    });
    if (wahl.art === "abbrechen") return null;
    if (wahl.art === "mitAnhaengen" && wahl.alle) return legeZugAbSichtbar({ ...zug, ohneAnhaenge: false }, ziel, api, neuLaden);

    const nurMail = await legeZugAbSichtbar({ ...zug, ohneAnhaenge: true }, ziel, api, neuLaden);
    if (wahl.art === "nurMail" || nurMail.abgelegt === 0) return nurMail;

    const gewaehlt = new Set(wahl.schluessel);
    const anhaenge = info.anhaenge.filter((a) => gewaehlt.has(a.id));
    if (anhaenge.length === 0) return nurMail;
    const mitAnhaengen = await legeZugAbSichtbar(
        {
            art: "anhang",
            anhaenge: {
                mailbox: zug.mail.mailbox,
                anhaenge: anhaenge.map((a) => ({
                    messageId: a.messageId || zug.mail.messageId,
                    attachmentId: a.id,
                    name: a.name,
                    contentType: a.contentType || null,
                    size: Number.isFinite(a.size) ? a.size : null,
                })),
            },
        },
        ziel,
        api,
        neuLaden,
    );
    return verbinde(nurMail, mitAnhaengen);
}

export type MailDateiNachUpload = {
    /** Abgelegte bzw. schon vorhandene Anhang-Dokumente. */
    anhaenge: number;
    fehler: string | null;
    documentIds: number[];
    /** Der Nutzer wollte nur die Mail. */
    nurMail?: boolean;
};

/**
 * Nach dem Hochladen einer gezogenen Datei: Ist es eine Outlook-Mail
 * (`.msg`/`.eml`) mit Anhängen, fragen, ob sie zusätzlich abgelegt werden —
 * und dann auspacken (alle oder die gewählten). Wirft nie; `null` = keine
 * Mail-Datei.
 */
export async function nachMailDateiUploadMitFrage(
    datei: { name: string },
    dokument: { id?: number | null } | null | undefined,
    frage: AnhangFrage = frageNachAnhaengen,
): Promise<MailDateiNachUpload | null> {
    if (!istMailDatei(datei.name) || dokument?.id == null) return null;
    let anhaenge;
    let betreff = "";
    try {
        const mail = await mailFileService.fetchParsed(dokument.id);
        anhaenge = (mail.attachments ?? []).filter((a) => !a.isInline);
        betreff = mail.subject ?? "";
    } catch {
        // Nicht lesbar → wie bisher: auspacken, was der Server findet.
        return nachMailDateiUpload(datei, dokument);
    }
    if (anhaenge.length === 0) return { anhaenge: 0, fehler: null, documentIds: [] };

    const wahl = await frage({
        mailName: betreff.trim() || datei.name,
        anhaenge: anhaenge.map((a) => ({ schluessel: String(a.index), name: a.name, groesse: a.size })),
        mailLiegtSchon: true,
    });
    if (wahl.art !== "mitAnhaengen") return { anhaenge: 0, fehler: null, documentIds: [], nurMail: true };
    try {
        const r = await mailDateiAuspacken(dokument.id, wahl.alle ? undefined : wahl.schluessel.map(Number));
        return {
            anhaenge: r.documentIds.length,
            fehler: r.failed ? `${r.failed} Anhang/Anhänge von „${datei.name}“ ließen sich nicht übernehmen.` : null,
            documentIds: r.documentIds,
        };
    } catch (err) {
        return { anhaenge: 0, fehler: err instanceof Error ? err.message : String(err), documentIds: [] };
    }
}
